import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './OurServices.css';

export default function OurServices() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.service-card-animate');
    if (!cards) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -80px 0px' }
    );
    cards.forEach(card => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: 'valuation',
      icon: (<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>),
      title: t('home.services.valuation.title'),
      desc: t('home.services.valuation.description'),
      cta: t('home.services.valuation.cta'),
      primary: true,
      handler: () => navigate('/valuation'),
      features: t('home.services.valuation.bullets'),
    },
    {
      id: 'renovation',
      icon: (<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/><path d="M9.5 9l1.5 1.5L14 8"/></svg>),
      title: t('home.services.renovation.title'),
      desc: t('home.services.renovation.description'),
      cta: t('home.services.renovation.cta'), primary: true,
      handler: () => navigate('/renovation'),
      features: t('home.services.renovation.bullets'),
    },
    {
      id: 'neighbourhood',
      icon: (<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>),
      title: t('home.services.neighbourhood.title'),
      desc: t('home.services.neighbourhood.description'),
      cta: t('home.services.neighbourhood.cta'),
      pro: true,
      handler: () => navigate('/neighbourhood'),
      features: t('home.services.neighbourhood.bullets'),
    },
    {
      id: 'mortgage',
      icon: (<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="10" y2="18"/><line x1="14" y1="18" x2="16" y2="18"/></svg>),
      title: t('home.services.mortgage.title'),
      desc: t('home.services.mortgage.description'),
      cta: t('home.services.mortgage.cta'), primary: true,
      handler: () => navigate('/mortgage'),
      features: t('home.services.mortgage.bullets'),
    },
  ];

  return (
    <section className="services" ref={sectionRef}>
      <div className="services__inner">
        <div className="services__header">
          <div className="services__eyebrow">
            <span className="services__eyebrow-line" />
            <span className="services__eyebrow-text">{t('home.sectionLabel')}</span>
            <span className="services__eyebrow-line" />
          </div>
          <h2 className="services__title">{t('home.services.title').split(' ').map((word, i) =>
            i === t('home.services.title').split(' ').length - 1
              ? <span key={i}>{word}</span>
              : word + ' '
          )}</h2>
          <p className="services__sub">{t('home.services.subtitle')}</p>
        </div>

        <div className="services__grid">
          {services.map((s, i) => (
            <div key={s.id} className="service-card service-card-animate" data-card-index={String(i)}>
              <div className="service-card__top">
                <div className="service-card__icon">{s.icon}</div>
                {s.badge && (
                  <span className={'service-card__badge service-card__badge--' + s.badgeType}>{s.badge}</span>
                )}
              </div>
              <h3 className="service-card__title">{s.title}</h3>
              <p className="service-card__desc">{s.desc}</p>
              <ul className="service-card__features">
                {Array.isArray(s.features) && s.features.map((f, i) => (
                  <li key={i}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    {f}
                  </li>
                ))}
              </ul>
              <div className="service-card__cta-wrap">
                <button
                  className={
                    'service-card__cta' +
                    (s.primary ? ' service-card__cta--primary' : '') +
                    (s.pro ? ' service-card__cta--pro' : '')
                  }
                  onClick={s.handler}
                >
                  {s.cta}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
